import { useQuery } from '@apollo/client';
import { Delete } from '@mui/icons-material';
import { Autocomplete, ListItemText, Popper, TextField, styled } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '~/components/IconButton';
import { namedByPlaceholder } from '~/components/forms/autocomplete-label';
import { useAppContext } from '~/contexts';
import type { SavedFilters_DeviceDynamicCollection as SavedFilter } from './SavedFilterAutocomplete.generated';
import { SavedFiltersDocument } from './SavedFilterAutocomplete.generated';

const WidePopper = styled(Popper)({
  width: '300px !important',
  marginLeft: '77px !important',
});

export interface SavedFilterAutocompleteProps {
  onChange: (filter: SavedFilter | null) => void;
  onDelete: (filter: SavedFilter) => void;
  value: SavedFilter | null;
}

export const SavedFilterAutocomplete = ({
  onChange,
  onDelete,
  value,
}: SavedFilterAutocompleteProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const { data, loading } = useQuery(SavedFiltersDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const filters = useMemo(() => data?.network?.savedFilters ?? [], [data]);

  if (loading && !data?.network?.savedFilters) return null;

  return (
    <Autocomplete
      sx={{
        // disableClearable doesn't play nice when needing to set the value to null
        '& .MuiAutocomplete-clearIndicator': {
          display: 'none',
        },
      }}
      fullWidth
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={loading}
      noOptionsText={t('common.labels.no_options')}
      onChange={(_event, newValue) => onChange(newValue)}
      options={filters}
      PopperComponent={(props) => <WidePopper {...props} />}
      renderInput={(params) => (
        <TextField
          placeholder={t('pages.devices.list.filters.select_filter')}
          sx={{
            '.MuiOutlinedInput-root': { padding: 0.5, paddingRight: '30px !important' },
          }}
          {...namedByPlaceholder(params, t('pages.devices.list.filters.select_filter'))}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <ListItemText primary={option.name} />
          <IconButton
            label={t('common.actions.delete')}
            description={option.canDestroy.reasons?.fullMessages.join(', ')}
            disabled={!option.canDestroy.value}
            edge="end"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(option);
            }}
          >
            <Delete fontSize="small" color={option.canDestroy.value ? 'error' : 'disabled'} />
          </IconButton>
        </li>
      )}
      value={value}
    />
  );
};
